import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { CustomSelect, Button } from '@/components';
import { PlusIcon, GripVertical } from './Icons';
import trashIcon from '../assets/trashIcon.png';
import { useCurrentUserProfile } from '../hook/useCurrentUserProfile';
import { CarouselCardsSection } from './CarouselCardsSection';
import { CarouselPreviewStrip } from './CarouselPreviewStrip';
import {
  PHONE_PATTERN,
  URL_PATTERN,
  validateCreateTemplate,
} from '../utils/templateValidation';
import {
  buildCreateTemplatePayload,
  languageMap,
} from '../utils/buildCreateTemplatePayload';
import { validateWhatsAppTemplate } from '../api';
import { decodeHtmlEntities } from '@/common/utils';
import { renderWhatsAppText } from './renderWhatsAppText';

function createEmptyCard(headerType) {
  return {
    id: `${Date.now()}-${Math.random()}`,
    header_type: headerType,
    previewUrl: null,
    image_base64: '',
    video_base64: '',
    body_text: '',
    buttons: [],
  };
}

export function TemplateCreateForm({
  onCancel,
  companyId,
  createTemplateMutation,
}) {
  const { companyName, avatarUrl } = useCurrentUserProfile();
  const [templateName, setTemplateName] = useState('');
  const [category, setCategory] = useState('Marketing');
  const [templateType, setTemplateType] = useState('Standard');
  const [language, setLanguage] = useState('English (US)');
  const [cardHeaderType, setCardHeaderType] = useState('IMAGE');
  const [cards, setCards] = useState(() => [
    createEmptyCard('IMAGE'),
    createEmptyCard('IMAGE'),
  ]);
  const isCarousel = templateType === 'Carousel';
  const [headerType, setHeaderType] = useState('None'); // None, Text, Image, Document
  const [headerText, setHeaderText] = useState('');
  const [headerFile, setHeaderFile] = useState(null);
  const [bodyText, setBodyText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [buttons, setButtons] = useState([]);

  // Authentication states
  const [otpType, setOtpType] = useState('Copy Code');
  const [securityRecommendation, setSecurityRecommendation] = useState(true);
  const [expiryWarning, setExpiryWarning] = useState(false);
  const [expiryMinutes, setExpiryMinutes] = useState(10);
  const [authButtonText, setAuthButtonText] = useState('Copy Code');
  const [androidPackageName, setAndroidPackageName] = useState('');
  const [appSignatureHash, setAppSignatureHash] = useState('');
  const [autofillButtonText, setAutofillButtonText] = useState('Autofill');

  const isAuth = category === 'Authentication';

  const [draggedBtnId, setDraggedBtnId] = useState(null);

  const [inlineErrors, setInlineErrors] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const timer = setTimeout(async () => {
      const error = validateCreateTemplate({
        templateName,
        language,
        category,
        bodyText,
        buttons,
        templateType,
        cards,
        cardHeaderType,
        otpType,
        securityRecommendation,
        expiryWarning,
        expiryMinutes,
        authButtonText,
        androidPackageName,
        appSignatureHash,
        autofillButtonText,
      });

      if (error) {
        if (isDirty || hasAttemptedSubmit) {
          setInlineErrors({ general: error });
        } else {
          setInlineErrors(null);
        }
        return;
      }

      setIsValidating(true);
      try {
        const payload = buildCreateTemplatePayload({
          templateType,
          templateName,
          language,
          category,
          headerType,
          headerText,
          bodyText,
          footerText,
          buttons,
          cards,
          cardHeaderType,
          otpType,
          securityRecommendation,
          expiryWarning,
          expiryMinutes,
          authButtonText,
          androidPackageName,
          appSignatureHash,
          autofillButtonText,
        });
        await validateWhatsAppTemplate({ companyId, payload });
        setInlineErrors(null);
      } catch (err) {
        if (err.response?.data?.errors) {
          setInlineErrors(err.response.data.errors);
        } else {
          setInlineErrors({ general: err.message || 'Validation failed' });
        }
      } finally {
        setIsValidating(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    templateName,
    language,
    category,
    bodyText,
    buttons,
    templateType,
    cards,
    cardHeaderType,
    otpType,
    securityRecommendation,
    expiryWarning,
    expiryMinutes,
    authButtonText,
    androidPackageName,
    appSignatureHash,
    autofillButtonText,
    headerType,
    headerText,
    footerText,
    companyId,
    isDirty,
    hasAttemptedSubmit,
  ]);

  const handleApplyPreset = (presetType) => {
    const randomSuffix = Math.floor(1000 + Math.random() * 9000);
    setTemplateName(`auth_template_${randomSuffix}`);
    setCategory('Authentication');
    setTemplateType('Standard');
    setOtpType('Copy Code');
    if (presetType === 'auth_otp_copy_code') {
      setSecurityRecommendation(true);
      setExpiryWarning(true);
      setExpiryMinutes(10);
      setAuthButtonText('Copy Code');
    } else if (presetType === 'auth_otp_basic') {
      setSecurityRecommendation(false);
      setExpiryWarning(false);
      setAuthButtonText('Copy Code');
    }
    setIsDirty(true);
    toast.success('Preset applied');
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    const cleaned = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setTemplateName(cleaned);
  };

  const handleAddButton = () => {
    if (buttons.length >= 3) {
      toast.error('You can add a maximum of 3 buttons.');
      return;
    }
    const newButton = {
      id: Date.now(),
      type: 'quick_reply',
      text: '',
      value: '',
    };
    setButtons([...buttons, newButton]);
  };

  const handleUpdateButton = (id, field, value) => {
    setButtons(
      buttons.map((btn) => (btn.id === id ? { ...btn, [field]: value } : btn)),
    );
  };

  const handleRemoveButton = (id) => {
    setButtons(buttons.filter((btn) => btn.id !== id));
  };

  const handleClearAll = (showToast = true) => {
    setTemplateName('');
    setCategory('Marketing');
    setTemplateType('Standard');
    setLanguage('English (US)');
    setHeaderType('None');
    setHeaderText('');
    setHeaderFile(null);
    setBodyText('');
    setFooterText('');
    setButtons([]);
    setCardHeaderType('IMAGE');
    cards.forEach((card) => {
      if (card.previewUrl) URL.revokeObjectURL(card.previewUrl);
    });
    setCards([createEmptyCard('IMAGE'), createEmptyCard('IMAGE')]);

    setOtpType('Copy Code');
    setSecurityRecommendation(true);
    setExpiryWarning(false);
    setExpiryMinutes(10);
    setAuthButtonText('Copy Code');
    setAndroidPackageName('');
    setAppSignatureHash('');
    setAutofillButtonText('Autofill');

    if (showToast) {
      toast.success('Form cleared successfully.');
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedBtnId(buttons[index].id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index.toString());
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Required to allow dropping
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    const draggedIndexStr = e.dataTransfer?.getData('text/plain');
    if (!draggedIndexStr) return;

    const draggedIndex = parseInt(draggedIndexStr, 10);

    if (draggedIndex !== index && !isNaN(draggedIndex)) {
      setButtons((prev) => {
        const newButtons = [...prev];
        const draggedItem = newButtons[draggedIndex];
        newButtons.splice(draggedIndex, 1);
        newButtons.splice(index, 0, draggedItem);
        return newButtons;
      });
    }
    setDraggedBtnId(null);
  };

  const handleDragEnd = () => {
    setDraggedBtnId(null);
  };

  const handleSaveTemplate = () => {
    setHasAttemptedSubmit(true);
    const error = validateCreateTemplate({
      templateName,
      language,
      category,
      bodyText,
      buttons,
      templateType,
      cards,
      cardHeaderType,
      otpType,
      securityRecommendation,
      expiryWarning,
      expiryMinutes,
      authButtonText,
      androidPackageName,
      appSignatureHash,
      autofillButtonText,
    });
    if (error) {
      toast.error(error);
      return;
    }

    const payload = buildCreateTemplatePayload({
      templateType,
      templateName,
      language,
      category,
      headerType,
      headerText,
      bodyText,
      footerText,
      buttons,
      cards,
      cardHeaderType,
      otpType,
      securityRecommendation,
      expiryWarning,
      expiryMinutes,
      authButtonText,
      androidPackageName,
      appSignatureHash,
      autofillButtonText,
    });

    createTemplateMutation.mutate(payload);
  };

  const parseMarkdown = (text) => {
    if (!text) return null;
    return renderWhatsAppText(decodeHtmlEntities(text), (id) => (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-primary/15 border border-primary/20 text-xxs font-mono font-bold text-primary mx-0.5">
        {id}
      </span>
    ));
  };

  return (
    <div className="flex flex-col xl:flex-row gap-5 2xl:gap-6 w-full min-w-0 max-w-full mx-auto animate-fadeIn relative items-start">
      {/* LEFT SECTION: Header + Builder Form */}
      <div className="w-full xl:flex-1 xl:min-w-0 flex flex-col gap-4">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between w-full shrink-0">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center justify-center w-9 h-9 rounded-[8px] bg-white border border-primary text-primary hover:bg-surface-mint transition-colors focus:outline-none shrink-0"
              aria-label="Back to Template List"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.25}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-main-black">
              Create Template
            </h1>
          </div>
        </div>

        {/* Builder Form Container */}
        <div className="w-full flex flex-col gap-4 laptop:gap-3.5">
          {/* Step 1: Basic Information */}
          <div className="flex flex-col gap-[10px] laptop:gap-2 animate-fadeIn">
            <div className="flex items-center gap-2">
              <h3 className="text-[18px] leading-[26px] laptop:text-[18px] laptop:leading-5 font-medium text-primary font-sf-pro-display">
                Basic Information
              </h3>
            </div>

            <div className="bg-form-background-light rounded-[12px] p-4 laptop:p-3 flex flex-col gap-3 laptop:gap-2.5">
              <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
                <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                  Template Name
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={handleNameChange}
                  placeholder="e.g. promo_summer_sale"
                  className="w-full h-[38px] laptop:h-9 px-3 rounded-input bg-white border border-surface-mint text-text-primary text-sm font-sf-pro-display placeholder-text-muted focus:outline-none focus:border-primary transition-all"
                />
                <span className="text-xs text-text-muted">
                  Lowercase and underscores only
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 laptop:gap-y-2.5 gap-x-3">
                <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
                  <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                    Category
                  </label>
                  <CustomSelect
                    value={category}
                    onChange={(val) => {
                      setCategory(val);
                      if (val !== 'Marketing') {
                        setTemplateType('Standard');
                      }
                    }}
                    options={[
                      { value: 'Marketing', label: 'Marketing' },
                      { value: 'Utility', label: 'Utility' },
                      { value: 'Authentication', label: 'Authentication' },
                    ]}
                    disabled={createTemplateMutation?.isPending}
                    className="w-full h-[38px] laptop:h-9"
                    buttonClassName="w-full h-full flex items-center justify-between rounded-input bg-white border border-surface-mint text-text-primary text-sm font-sf-pro-display focus:outline-none transition-all px-3 disabled:opacity-50"
                    textSizeClass="text-sm"
                  />
                </div>
                <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
                  <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                    Language
                  </label>
                  <CustomSelect
                    value={language}
                    onChange={(val) => setLanguage(val)}
                    options={Object.keys(languageMap).map((lang) => ({
                      value: lang,
                      label: lang,
                    }))}
                    disabled={createTemplateMutation?.isPending}
                    className="w-full h-[38px] laptop:h-9"
                    buttonClassName="w-full h-full flex items-center justify-between rounded-input bg-white border border-surface-mint text-text-primary text-sm font-sf-pro-display focus:outline-none transition-all px-3 disabled:opacity-50"
                    textSizeClass="text-sm"
                  />
                </div>
              </div>
              {category === 'Marketing' && (
                <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
                  <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                    Template Type
                  </label>
                  <div className="grid grid-cols-2 min-[460px]:grid-cols-4 gap-2">
                    {['Standard', 'Carousel'].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          setTemplateType(type);
                          if (type === 'Carousel') {
                            setCategory('Marketing');
                          }
                        }}
                        className={`h-[38px] laptop:h-9 px-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center text-center select-none ${
                          templateType === type
                            ? 'bg-primary text-white shadow-md'
                            : 'bg-white border border-surface-mint text-text-secondary hover:text-text-primary'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Step 2: Header */}
          {!isCarousel && !isAuth && (
            <div className="flex flex-col gap-[10px] laptop:gap-2 animate-fadeIn">
              <div className="flex items-center gap-2">
                <h3 className="text-[18px] leading-[26px] laptop:text-[18px] laptop:leading-5 font-medium text-primary font-sf-pro-display">
                  Header
                </h3>
              </div>

              <div className="bg-form-background-light rounded-[12px] p-4 laptop:p-3 flex flex-col gap-3 laptop:gap-2.5">
                <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
                  <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                    Header Type
                  </label>
                  <div className="grid grid-cols-2 min-[460px]:grid-cols-4 gap-2">
                    {['None', 'Text', 'Image', 'Document'].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          setHeaderType(type);
                          setHeaderFile(null);
                        }}
                        className={`h-[38px] laptop:h-9 px-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center text-center select-none ${
                          headerType === type
                            ? 'bg-primary text-white shadow-md'
                            : 'bg-white border border-surface-mint text-text-secondary hover:text-text-primary'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {headerType === 'Text' && (
                  <div className="flex flex-col gap-1.5 laptop:gap-[5px] animate-fadeIn">
                    <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                      Header Text
                    </label>
                    <input
                      type="text"
                      value={headerText}
                      onChange={(e) =>
                        setHeaderText(e.target.value.slice(0, 60))
                      }
                      placeholder="Enter header text... (max 60 chars)"
                      className="w-full h-[38px] laptop:h-9 px-3 rounded-input bg-white border border-surface-mint text-text-primary text-sm font-sf-pro-display placeholder-text-muted focus:outline-none focus:border-primary transition-all"
                    />
                    <div className="flex justify-end text-xxs text-text-muted -mt-1">
                      <span>{headerText.length} / 60</span>
                    </div>
                  </div>
                )}

                {(headerType === 'Image' || headerType === 'Document') && (
                  <div className="flex flex-col gap-1.5 laptop:gap-[5px] animate-fadeIn">
                    <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                      Upload {headerType}
                    </label>
                    <div className="w-full border border-dashed border-surface-mint rounded-2xl p-5 laptop:p-3 bg-white transition-all flex flex-col items-center justify-center text-center relative hover:border-primary/30 cursor-pointer">
                      <input
                        type="file"
                        accept={
                          headerType === 'Image' ? 'image/*' : 'application/pdf'
                        }
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setHeaderFile(
                              URL.createObjectURL(e.target.files[0]),
                            );
                            toast.success(`${headerType} selected.`);
                          }
                        }}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                      <div className="w-10 h-10 laptop:w-8 laptop:h-8 rounded-full bg-form-background-light border border-surface-mint flex items-center justify-center text-text-secondary mb-2 laptop:mb-1">
                        {headerType === 'Image' ? (
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <rect
                              width="18"
                              height="18"
                              x="3"
                              y="3"
                              rx="2"
                              strokeWidth="2"
                            />
                            <circle cx="9" cy="9" r="2" strokeWidth="2" />
                            <path
                              strokeWidth="2"
                              strokeLinecap="round"
                              d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-xs text-text-secondary font-bold">
                        Click to upload a mockup {headerType.toLowerCase()}
                      </span>
                      <span className="text-xxs text-text-muted mt-1">
                        {headerFile
                          ? 'File attached'
                          : `PNG, JPG or PDF formats`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Authentication Settings */}
          {isAuth && (
            <div className="flex flex-col gap-[10px] laptop:gap-2 animate-fadeIn">
              <h3 className="text-[18px] leading-[26px] laptop:text-[18px] laptop:leading-5 font-medium text-primary font-sf-pro-display">
                Authentication Settings
              </h3>
              <div className="bg-form-background-light rounded-[12px] p-4 laptop:p-3 flex flex-col gap-4 laptop:gap-2.5">
                <div className="flex mb-2 laptop:mb-1">
                  <div className="flex flex-col gap-1.5 laptop:gap-[5px] justify-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={securityRecommendation}
                        onChange={(e) =>
                          setSecurityRecommendation(e.target.checked)
                        }
                        className="w-4 h-4 rounded border-surface-mint text-primary focus:ring-primary"
                        // {
                        //   value: 'One Tap Autofill',
                        //   label: 'One Tap Autofill',
                        // },
                        // { value: 'Zero Tap', label: 'Zero Tap' },
                      />
                      <span className="text-sm font-semibold text-text-secondary">
                        Security recommendation
                      </span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 laptop:gap-2.5 mt-2 laptop:mt-1">
                  <div
                    className="p-3 border border-border-default rounded-[8px] cursor-pointer hover:border-primary transition-all bg-white"
                    onClick={() => handleApplyPreset('auth_otp_copy_code')}
                  >
                    <h3 className="font-bold text-sm text-text-primary mb-1">
                      Standard OTP with Copy Code
                    </h3>
                    <p className="text-xs text-text-secondary">
                      Copy Code button, security disclaimer, 10-min expiration.
                    </p>
                  </div>
                  <div
                    className="p-3 border border-border-default rounded-[8px] cursor-pointer hover:border-primary transition-all bg-white"
                    onClick={() => handleApplyPreset('auth_otp_basic')}
                  >
                    <h3 className="font-bold text-sm text-text-primary mb-1">
                      Basic OTP
                    </h3>
                    <p className="text-xs text-text-secondary">
                      Copy Code button and no expiration.
                    </p>
                  </div>
                </div>

                {otpType === 'Zero Tap' && (
                  <div className="bg-[#F0F2F5] rounded-[8px] p-3 border border-border-default mt-1">
                    <span className="text-sm font-semibold text-text-primary block mb-1">
                      Zero-Tap Authentication
                    </span>
                    <span className="text-xs text-text-secondary">
                      Zero-Tap delivers verification codes seamlessly in the
                      background without user interaction using handshake-based
                      delivery. No buttons are required.
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 laptop:gap-y-2.5 gap-x-4 border-t border-surface-mint pt-4 laptop:pt-3 mt-2 laptop:mt-1">
                  <div className="flex flex-col gap-2 col-span-1 md:col-span-2">
                    <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary mb-1">
                      Code expires in (minutes)
                    </label>
                    <div className="flex flex-wrap gap-2 items-center">
                      {['None', '5', '10', '15', '30'].map((pill) => {
                        const isSelected =
                          pill === 'None'
                            ? !expiryWarning
                            : expiryWarning && String(expiryMinutes) === pill;
                        return (
                          <button
                            key={pill}
                            type="button"
                            onClick={() => {
                              if (pill === 'None') {
                                setExpiryWarning(false);
                              } else {
                                setExpiryWarning(true);
                                setExpiryMinutes(pill);
                              }
                            }}
                            className={`px-4 py-1.5 laptop:h-9 laptop:py-0 rounded-full text-xs font-semibold transition-all border ${isSelected ? 'bg-primary text-white border-primary' : 'bg-white text-text-secondary border-border-default hover:border-primary/50'}`}
                          >
                            {pill === 'None' ? 'None' : `${pill} min`}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {otpType !== 'Zero Tap' && (
                    <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
                      <div className="flex items-center justify-between">
                        <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                          Copy Button Text
                        </label>
                        <span className="text-xs text-text-muted">
                          {authButtonText.length}/25
                        </span>
                      </div>
                      <input
                        type="text"
                        value={authButtonText}
                        onChange={(e) =>
                          setAuthButtonText(e.target.value.slice(0, 25))
                        }
                        placeholder="e.g. Copy Code"
                        className="w-full h-[38px] laptop:h-9 px-3 rounded-input bg-white border border-surface-mint text-text-primary text-sm font-sf-pro-display focus:outline-none focus:border-primary transition-all"
                      />
                    </div>
                  )}
                </div>

                {otpType === 'One Tap Autofill' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 laptop:gap-y-2.5 gap-x-4 border-t border-surface-mint pt-4 laptop:pt-3 mt-2 laptop:mt-1">
                    <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
                      <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                        Android Package Name
                      </label>
                      <input
                        type="text"
                        value={androidPackageName}
                        onChange={(e) => setAndroidPackageName(e.target.value)}
                        placeholder="e.g. com.company.app"
                        className="w-full h-[38px] laptop:h-9 px-3 rounded-input bg-white border border-surface-mint text-text-primary text-sm font-sf-pro-display focus:outline-none focus:border-primary transition-all"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
                      <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                        App Signature Hash
                      </label>
                      <input
                        type="text"
                        value={appSignatureHash}
                        onChange={(e) => setAppSignatureHash(e.target.value)}
                        className="w-full h-[38px] laptop:h-9 px-3 rounded-input bg-white border border-surface-mint text-text-primary text-sm font-sf-pro-display focus:outline-none focus:border-primary transition-all"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
                      <div className="flex items-center justify-between">
                        <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                          Autofill Button Text
                        </label>
                        <span className="text-xs text-text-muted">
                          {autofillButtonText.length}/25
                        </span>
                      </div>
                      <input
                        type="text"
                        value={autofillButtonText}
                        onChange={(e) =>
                          setAutofillButtonText(e.target.value.slice(0, 25))
                        }
                        placeholder="e.g. Autofill"
                        className="w-full h-[38px] laptop:h-9 px-3 rounded-input bg-white border border-surface-mint text-text-primary text-sm font-sf-pro-display focus:outline-none focus:border-primary transition-all"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Body & Footer Text */}
          {!isAuth && (
            <div className="flex flex-col gap-[10px] laptop:gap-2 animate-fadeIn">
              <div className="flex items-center gap-2">
                <h3 className="text-[18px] leading-[26px] laptop:text-[18px] laptop:leading-5 font-medium text-primary font-sf-pro-display">
                  {isCarousel ? 'Body Text' : 'Body & Footer Text'}
                </h3>
              </div>

              <div className="bg-form-background-light rounded-[12px] p-4 laptop:p-3 flex flex-col gap-4 laptop:gap-2.5">
                <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
                  <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                    Body Text (Required)
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={bodyText}
                    onChange={(e) => setBodyText(e.target.value.slice(0, 1024))}
                    placeholder="Type template body text"
                    rows={4}
                    className="w-full p-4 laptop:p-3 rounded-input bg-white border border-surface-mint text-text-primary text-sm font-sf-pro-display placeholder-text-muted focus:outline-none focus:border-primary transition-all resize-none no-scrollbar"
                  />
                  <div className="flex justify-end text-xxs text-text-muted -mt-1">
                    <span>{bodyText.length} / 1024</span>
                  </div>
                </div>

                {!isCarousel && (
                  <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
                    <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                      Footer Text
                    </label>
                    <input
                      type="text"
                      value={footerText}
                      onChange={(e) =>
                        setFooterText(e.target.value.slice(0, 60))
                      }
                      placeholder="Enter footer text... (max 60 chars)"
                      className="w-full h-[38px] laptop:h-9 px-3 rounded-input bg-white border border-surface-mint text-text-primary text-sm font-sf-pro-display placeholder-text-muted focus:outline-none focus:border-primary transition-all"
                    />
                    <div className="flex justify-end text-xxs text-text-muted -mt-1">
                      <span>{footerText.length} / 60</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Buttons (Max 3) */}
          {!isCarousel && !isAuth && (
            <div className="flex flex-col gap-[10px] laptop:gap-2 animate-fadeIn">
              <h3 className="text-[18px] leading-[26px] laptop:text-[18px] laptop:leading-5 font-medium text-primary font-sf-pro-display">
                Buttons (Max 3)
              </h3>

              <div className="bg-form-background-light rounded-[12px] p-4 laptop:p-3 flex flex-col gap-3 laptop:gap-2.5">
                {buttons.map((btn, index) => (
                  <div
                    key={btn.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    className={`p-4 laptop:p-3 rounded-2xl bg-white border border-surface-mint transition-all flex flex-col gap-3 laptop:gap-2.5 relative ${
                      draggedBtnId === btn.id
                        ? 'opacity-40 cursor-grabbing'
                        : 'hover:border-primary/20 animate-fadeIn'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3 relative min-h-[32px] laptop:min-h-0">
                      <div className="flex items-center gap-2">
                        <div
                          className="cursor-grab hover:bg-black/5 p-1 rounded transition-colors"
                          title="Drag to reorder"
                        >
                          <GripVertical className="w-5 h-5 text-text-muted" />
                        </div>
                        <span className="text-[15px] font-semibold text-primary">
                          Button {index + 1}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveButton(btn.id)}
                        className="p-1 hover:bg-red-500/10 rounded-lg transition-all self-start mt-[-4px] mr-[-4px]"
                      >
                        <img
                          src={trashIcon}
                          alt="Delete"
                          className="w-4 h-4 object-contain"
                        />
                      </button>
                    </div>

                    {/* Type and text configuration */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 laptop:gap-y-2.5 gap-x-3">
                      <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
                        <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                          Button Type
                        </label>
                        <CustomSelect
                          value={btn.type}
                          onChange={(val) =>
                            handleUpdateButton(btn.id, 'type', val)
                          }
                          options={[
                            { value: 'quick_reply', label: 'Quick Reply' },
                            { value: 'url', label: 'Call to Action (URL)' },
                            {
                              value: 'phone',
                              label: 'Call to Action (Phone)',
                            },
                          ]}
                          className="w-full h-[38px] laptop:h-9"
                          buttonClassName="w-full h-full flex items-center justify-between rounded-input bg-form-background-light border-0 text-text-primary text-sm font-sf-pro-display focus:outline-none transition-all px-3"
                          textSizeClass="text-sm"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5 laptop:gap-[5px] pr-8">
                        <div className="flex items-center justify-between">
                          <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                            Button Text
                          </label>
                          <span className="text-xs text-text-muted">
                            {btn.text.length}/25
                          </span>
                        </div>
                        <input
                          type="text"
                          value={btn.text}
                          onChange={(e) =>
                            handleUpdateButton(
                              btn.id,
                              'text',
                              e.target.value.slice(0, 25),
                            )
                          }
                          placeholder="e.g. Visit Website"
                          className="w-full h-[38px] laptop:h-9 px-3 rounded-input bg-form-background-light border-0 text-text-primary text-sm font-sf-pro-display placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-primary transition-all"
                        />
                      </div>
                    </div>

                    {/* URL or Phone Input field depending on type */}
                    {btn.type !== 'quick_reply' &&
                      (() => {
                        const isUrl = btn.type === 'url';
                        const val = btn.value.trim();
                        let errorMsg = '';

                        if (val) {
                          if (isUrl) {
                            const urlPattern = URL_PATTERN;
                            if (!urlPattern.test(val)) {
                              errorMsg =
                                'Please enter a valid URL (e.g. https://example.com)';
                            }
                          } else {
                            const phonePattern = PHONE_PATTERN;
                            const cleanPhone = val.replace(/[\s-]/g, '');
                            if (!phonePattern.test(cleanPhone)) {
                              errorMsg =
                                'Please enter a valid phone number (e.g. +919876543210)';
                            }
                          }
                        }

                        return (
                          <div className="flex flex-col gap-1.5 laptop:gap-[5px] animate-fadeIn pr-8 mt-1">
                            <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                              {isUrl ? 'URL Address' : 'Phone Number'}
                            </label>
                            <input
                              type="text"
                              value={btn.value}
                              onChange={(e) =>
                                handleUpdateButton(
                                  btn.id,
                                  'value',
                                  e.target.value,
                                )
                              }
                              placeholder={
                                isUrl
                                  ? 'e.g. https://example.com'
                                  : 'e.g. +91 9876543210'
                              }
                              className={`w-full h-[38px] laptop:h-9 px-3 rounded-input bg-form-background-light text-text-primary text-sm font-sf-pro-display placeholder-text-muted focus:outline-none transition-all ${
                                errorMsg
                                  ? 'border border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                                  : 'border-0 focus:ring-1 focus:ring-primary'
                              }`}
                            />
                            {errorMsg && (
                              <span className="text-xxs text-red-500 font-medium animate-fadeIn">
                                {errorMsg}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={handleAddButton}
                  disabled={buttons.length >= 3}
                  className="flex items-center justify-center gap-2 h-12 laptop:h-10 rounded-2xl border-2 border-dashed border-primary/40 text-primary text-sm font-semibold hover:bg-surface-mint transition-all disabled:opacity-30 disabled:pointer-events-none"
                >
                  <span>Add Button</span>
                  <PlusIcon />
                </button>
              </div>
            </div>
          )}

          {!isAuth && isCarousel && (
            <CarouselCardsSection
              cards={cards}
              cardHeaderType={cardHeaderType}
              onHeaderTypeChange={setCardHeaderType}
              onCardsChange={setCards}
              disabled={createTemplateMutation?.isPending}
            />
          )}
        </div>
      </div>

      {/* RIGHT SECTION: Mobile Preview + Action Buttons */}
      <div className="w-full max-w-full xl:w-[320px] 2xl:w-[350px] shrink-0 self-start xl:sticky xl:top-0 xl:max-h-[calc(100dvh-1.5rem)] xl:overflow-y-auto flex flex-col items-center gap-3.5 p-3 bg-form-background-light rounded-2xl border border-surface-mint shadow-xs">
        <div className="w-full max-w-[260px] 2xl:max-w-[275px] h-[min(480px,calc(100dvh-240px))] 2xl:h-[min(530px,calc(100dvh-220px))] shrink-0 rounded-[32px] border-[7.5px] border-phone-bezel bg-white relative flex flex-col overflow-hidden select-none border-t-[10px] border-b-[10px] shadow-lg">
          {/* Front Camera Notch */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-22 h-3.5 bg-phone-bezel rounded-b-[12px] z-50 flex items-center justify-evenly px-4 pointer-events-none">
            <div className="w-5 h-0.5 bg-[#333] rounded-full" />
            <div className="w-1.5 h-1.5 rounded-full bg-black" />
          </div>

          {/* Single Scrollable Mobile Screen */}
          <div className="w-full h-full flex-1 flex flex-col overflow-y-auto custom-scrollbar overscroll-contain min-h-0 bg-[#F7F8FA]">
            {/* Mock Status Bar */}
            <div className="h-5 w-full flex items-center justify-between px-3 text-[9px] font-bold text-[#111B21] z-40 bg-white select-none pt-0.5 shrink-0">
              <span>09:41</span>
              <div className="flex items-center gap-1.5">
                <svg className="w-2.5 h-2.5 fill-current" viewBox="0 0 24 24">
                  <path d="M2 22h20V2z" />
                </svg>
                <svg className="w-2.5 h-2.5 fill-current" viewBox="0 0 24 24">
                  <path d="M12 21a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM4.8 13.8a10 10 0 0 1 14.4 0m-11.5 4a6 6 0 0 1 8.6 0" />
                </svg>
                <div className="w-3.5 h-2 border border-[#111B21]/60 rounded-sm flex items-center p-0.5 relative">
                  <div className="h-full w-3/4 bg-[#111B21]/80 rounded-[1px]" />
                </div>
              </div>
            </div>

            {/* WhatsApp Header */}
            <div className="bg-white px-2.5 py-1.5 flex items-center justify-between text-[#111B21] border-b border-border-default z-30 select-none shrink-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <svg
                  className="w-3.5 h-3.5 text-[#111B21] shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 19.5L8.25 12l7.5-7.5"
                  />
                </svg>

                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt=""
                    className="w-6 h-6 rounded-full object-cover border border-status-info/30 shrink-0"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-status-info/15 border border-status-info/30 flex items-center justify-center font-bold text-tiny text-status-info tracking-wider shrink-0">
                    {companyName.charAt(0).toUpperCase()}
                  </div>
                )}

                <div className="text-left flex flex-col justify-center min-w-0">
                  <div className="flex items-center gap-0.5 min-w-0">
                    <span className="text-tiny font-bold leading-none truncate block max-w-[110px]">
                      {companyName}
                    </span>
                    <svg className="w-2.5 h-2.5 shrink-0" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" fill="#53bdeb" />
                      <path
                        d="M9 12.5l2 2 4-4.5"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  </div>
                  <span className="text-pico text-[#8696A0] leading-none mt-0.5">
                    Online
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-[#111B21] shrink-0">
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
                  />
                </svg>
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.387a12.035 12.035 0 01-7.108-7.108c-.155-.44.01-.928.387-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                  />
                </svg>
              </div>
            </div>

            {/* Mock Chat Window Feed Area */}
            <div className="w-full flex-1 flex flex-col gap-1 p-2 bg-[#F7F8FA] justify-start overflow-visible">
              <div className="self-stretch bg-[#FEF6D8] text-[#7A6A2E] text-nano leading-tight text-center py-0.5 px-1.5 rounded-md">
                Messages to this chat and calls are now secured with end-to-end
                encryption. Tap to learn more.
              </div>

              <div className="self-center bg-white border border-border-default text-[#8696A0] text-nano font-bold uppercase py-0.5 px-2 rounded-md shadow-xs">
                Today
              </div>

              <div className="flex flex-col items-start gap-1.5 w-full text-left">
                {isAuth ? (
                  <>
                    <div className="bg-white text-[#111B21] rounded-2xl rounded-tl-none p-2.5 shadow-sm max-w-[92%] flex flex-col w-fit border border-border-default relative z-10">
                      <span className="text-xxs text-[#111B21] leading-normal break-words whitespace-pre-wrap block max-w-[205px] xl:max-w-[220px]">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-primary/15 border border-primary/20 text-xxs font-mono font-bold text-primary mx-0.5">
                          1
                        </span>{' '}
                        is your verification code.
                        {securityRecommendation &&
                          '\n\nFor your security, do not share this code.'}
                      </span>
                      {expiryWarning && (
                        <span className="text-micro text-[#8696A0] mt-1 font-semibold block leading-tight break-words max-w-[205px] xl:max-w-[220px]">
                          This code expires in {expiryMinutes} minutes.
                        </span>
                      )}
                      <div className="flex items-center justify-end gap-0.5 mt-1 self-end">
                        <span className="text-pico text-[#8696A0] font-bold">
                          12:00 PM
                        </span>
                        <svg
                          className="w-2.5 h-2.5 text-[#53BDEB] fill-current"
                          viewBox="0 0 24 24"
                        >
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 mt-1.5 w-[205px] xl:w-[220px] max-w-full">
                      <button
                        type="button"
                        className="w-full h-7 rounded-lg bg-white border border-border-default hover:bg-[#F7F8FA] transition-all flex items-center justify-center gap-1 text-tiny text-status-info font-bold shadow-sm select-none"
                      >
                        <svg
                          className="w-3 h-3 text-status-info"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
                          />
                        </svg>
                        <span>{authButtonText || 'Copy Code'}</span>
                      </button>
                      {otpType === 'One Tap Autofill' && (
                        <button
                          type="button"
                          className="w-full h-7 rounded-lg bg-white border border-border-default hover:bg-[#F7F8FA] transition-all flex items-center justify-center gap-1 text-tiny text-status-info font-bold shadow-sm select-none"
                        >
                          <span>{autofillButtonText || 'Autofill'}</span>
                        </button>
                      )}
                    </div>
                  </>
                ) : isCarousel ? (
                  <>
                    <div className="bg-white text-[#111B21] rounded-2xl rounded-tl-none p-2.5 shadow-sm max-w-[92%] flex flex-col w-fit border border-border-default relative z-10">
                      <span className="text-xxs text-[#111B21] leading-normal break-words whitespace-pre-wrap block max-w-[205px] xl:max-w-[220px]">
                        {parseMarkdown(bodyText) || (
                          <span className="text-[#8696A0] italic">
                            Your body message template content goes here...
                          </span>
                        )}
                      </span>
                      <div className="flex items-center justify-end gap-0.5 mt-1 self-end">
                        <span className="text-pico text-[#8696A0] font-bold">
                          12:00 PM
                        </span>
                      </div>
                    </div>
                    <CarouselPreviewStrip
                      cards={cards.map((card) => ({
                        ...card,
                        header_type: cardHeaderType,
                      }))}
                      parseBody={parseMarkdown}
                      compact
                    />
                  </>
                ) : (
                  <>
                    {/* Bubble Container */}
                    <div className="bg-white text-[#111B21] rounded-2xl rounded-tl-none p-2.5 shadow-sm max-w-[92%] flex flex-col w-fit border border-border-default relative z-10">
                      {/* Header Mockup */}
                      {headerType !== 'None' && (
                        <div className="mb-1.5 pb-1.5 border-b border-border-default text-tiny leading-tight text-[#111B21] flex flex-col gap-1 w-full max-w-[205px] xl:max-w-[220px]">
                          {headerType === 'Text' && (
                            <span className="font-bold text-[#111B21] tracking-wide block truncate">
                              {headerText || (
                                <span className="text-[#8696A0] italic">
                                  Header text preview...
                                </span>
                              )}
                            </span>
                          )}

                          {headerType === 'Image' && (
                            <div className="w-full aspect-video rounded-lg bg-[#F0F2F5] border border-border-default flex items-center justify-center overflow-hidden">
                              {headerFile ? (
                                <img
                                  src={headerFile}
                                  alt="header preview"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="flex flex-col items-center gap-1 text-[#8696A0]">
                                  <svg
                                    className="w-3.5 h-3.5 text-[#8696A0]"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <rect
                                      width="18"
                                      height="18"
                                      x="3"
                                      y="3"
                                      rx="2"
                                      strokeWidth="2"
                                    />
                                    <circle
                                      cx="9"
                                      cy="9"
                                      r="2"
                                      strokeWidth="2"
                                    />
                                    <path
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"
                                    />
                                  </svg>
                                  <span className="text-pico font-semibold uppercase tracking-wider">
                                    Image Preview
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {headerType === 'Document' && (
                            <div className="w-full h-8 rounded-lg bg-[#F0F2F5] border border-border-default flex items-center gap-1.5 px-2 text-[#111B21]">
                              <svg
                                className="w-4 h-4 text-red-500 shrink-0"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              <div className="flex flex-col min-w-0">
                                <span className="text-micro font-bold truncate">
                                  Document_Preview.pdf
                                </span>
                                <span className="text-pico text-[#8696A0] leading-none">
                                  1.2 MB • PDF
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Body Text Mockup */}
                      <span className="text-xxs text-[#111B21] leading-normal break-words whitespace-pre-wrap block max-w-[205px] xl:max-w-[220px]">
                        {parseMarkdown(bodyText) || (
                          <span className="text-[#8696A0] italic">
                            Your body message template content goes here...
                          </span>
                        )}
                      </span>

                      {/* Footer Text Mockup */}
                      {footerText && (
                        <span className="text-micro text-[#8696A0] mt-1 font-semibold block leading-tight break-words max-w-[205px] xl:max-w-[220px]">
                          {footerText}
                        </span>
                      )}

                      {/* Timestamp & Read Tick */}
                      <div className="flex items-center justify-end gap-0.5 mt-1 self-end">
                        <span className="text-pico text-[#8696A0] font-bold">
                          12:00 PM
                        </span>
                        <svg
                          className="w-2.5 h-2.5 text-[#53BDEB] fill-current"
                          viewBox="0 0 24 24"
                        >
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      </div>
                    </div>

                    {/* Quick Reply & CTA Buttons Mockup */}
                    {buttons.length > 0 && (
                      <div className="flex flex-col gap-1 mt-1.5 w-[205px] xl:w-[220px] max-w-full">
                        {buttons.map((btn) => (
                          <button
                            key={btn.id}
                            type="button"
                            className="w-full h-7 rounded-lg bg-white border border-border-default hover:bg-[#F7F8FA] transition-all flex items-center justify-center gap-1 text-tiny text-status-info font-bold shadow-sm select-none"
                          >
                            {btn.type === 'url' && (
                              <svg
                                className="w-3 h-3 text-status-info"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                />
                              </svg>
                            )}
                            {btn.type === 'phone' && (
                              <svg
                                className="w-3 h-3 text-status-info"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.387a12.035 12.035 0 01-7.108-7.108c-.155-.44.01-.928.387-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                                />
                              </svg>
                            )}
                            <span>{btn.text || 'Button text...'}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Mock Bottom Input Bar (Pinned to Bottom of Phone Screen) */}
            <div className="bg-white p-1.5 flex items-center gap-1.5 text-[#8696A0] border-t border-border-default z-30 select-none shrink-0 mt-auto">
              <div className="flex-grow h-6.5 px-2.5 rounded-full bg-[#F0F2F5] flex items-center justify-between text-nano">
                <span className="text-[#8696A0]">Message</span>
              </div>
              <div className="w-6.5 h-6.5 rounded-full bg-primary flex items-center justify-center text-white shrink-0">
                <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {/* Action Buttons Row */}
        <div className="w-full flex flex-col gap-2 shrink-0 pt-3 border-t border-border-default/60">
          <div className="grid grid-cols-2 gap-2 w-full">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              className="w-full h-[34px] px-3 text-xs font-medium"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleClearAll()}
              className="w-full h-[34px] px-3 text-xs font-medium"
            >
              Clear All
            </Button>
          </div>
          <Button
            type="button"
            variant="primary"
            onClick={handleSaveTemplate}
            disabled={
              createTemplateMutation?.isPending ||
              isValidating ||
              inlineErrors !== null
            }
            className="w-full h-[36px] px-4 text-xs font-semibold disabled:opacity-50"
          >
            {createTemplateMutation?.isPending || isValidating ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>{isValidating ? 'Validating...' : 'Saving...'}</span>
              </>
            ) : (
              <>
                <span>Save Template</span>
                <span>→</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
